import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchZapiQrCode, fetchZapiStatus } from '../services/api.ts';
import {
  ATENDABOT_WHATSAPP_STATUS_EVENT,
  isAtendabotWhatsAppUnavailable,
  isAtendabotWhatsAppUnavailableError,
  WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE,
} from '../utils/whatsappStatus.ts';
import {
  QR_CODE_EXPIRATION_MS,
  ZAPI_STATUS_POLL_INTERVAL_MS,
} from '../constants/timing.js';

type UseAtendabotWhatsAppGateOptions = {
  restauranteId?: string | number | null;
  hasInstanceToken: boolean;
  loadingRestaurant: boolean;
  bypassProvisioningGate: boolean;
  /** Atualiza restaurante.whatsapp_instance_configured (mesmo efeito do Layout legado). */
  onConfigured?: () => void;
};

export function useAtendabotWhatsAppGate({
  restauranteId,
  hasInstanceToken,
  loadingRestaurant,
  bypassProvisioningGate,
  onConfigured,
}: UseAtendabotWhatsAppGateOptions) {
  const [zapiStatus, setZapiStatus] = useState<any>(null);
  const [checkingZapiStatus, setCheckingZapiStatus] = useState(false);
  const [hasZapiCheck, setHasZapiCheck] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [qrExpired, setQrExpired] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);

  useEffect(() => {
    const updateWhatsappStatus = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      if (typeof detail.configured !== 'boolean') return;

      const payload = { ...detail, checkedAt: Date.now() };
      setZapiStatus(payload);
      setHasZapiCheck(true);
      setCheckingZapiStatus(false);
      if (restauranteId) {
        try {
          sessionStorage.setItem(`atendabot-zapi-status-${restauranteId}`, JSON.stringify(payload));
        } catch (_) {}
      }

      if (detail.configured === true) {
        onConfigured?.();
      }
    };

    window.addEventListener(ATENDABOT_WHATSAPP_STATUS_EVENT, updateWhatsappStatus);
    return () => window.removeEventListener(ATENDABOT_WHATSAPP_STATUS_EVENT, updateWhatsappStatus);
  }, [restauranteId, onConfigured]);

  useEffect(() => {
    if (!restauranteId || !hasInstanceToken) {
      setZapiStatus(null);
      setHasZapiCheck(false);
      return;
    }

    const cacheKey = `atendabot-zapi-status-${restauranteId}`;
    const checkStatus = async () => {
      setCheckingZapiStatus(true);
      try {
        const status = await fetchZapiStatus(restauranteId);
        const payload = { ...status, checkedAt: Date.now() };
        setZapiStatus(payload);
        setHasZapiCheck(true);
        sessionStorage.setItem(cacheKey, JSON.stringify(payload));
      } catch (error) {
        const payload = {
          connected: false,
          smartphoneConnected: false,
          error: 'Falha ao verificar status do WhatsApp',
          checkedAt: Date.now(),
        };
        setZapiStatus(payload);
        setHasZapiCheck(true);
        sessionStorage.setItem(cacheKey, JSON.stringify(payload));
      } finally {
        setCheckingZapiStatus(false);
      }
    };

    checkStatus();
  }, [hasInstanceToken, restauranteId]);

  useEffect(() => {
    if (!restauranteId || !hasInstanceToken) return undefined;
    let checking = false;
    const intervalId = window.setInterval(async () => {
      if (checking || document.hidden) return;
      checking = true;
      try {
        const status = await fetchZapiStatus(restauranteId);
        const payload = { ...status, checkedAt: Date.now() };
        setZapiStatus(payload);
        setHasZapiCheck(true);
        sessionStorage.setItem(`atendabot-zapi-status-${restauranteId}`, JSON.stringify(payload));
      } catch (_) {
        // Mantém o último estado conhecido até a próxima consulta.
      } finally {
        checking = false;
      }
    }, 30_000);
    return () => window.clearInterval(intervalId);
  }, [hasInstanceToken, restauranteId]);

  useEffect(() => {
    if (!qrImage) return undefined;
    if (!restauranteId) return undefined;

    let polling = false;
    const intervalId = window.setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        const status = await fetchZapiStatus(restauranteId);
        const payload = { ...status, checkedAt: Date.now() };
        setZapiStatus(payload);
        setHasZapiCheck(true);
        sessionStorage.setItem(`atendabot-zapi-status-${restauranteId}`, JSON.stringify(payload));
        if (
          isAtendabotWhatsAppUnavailable(status)
          || (status?.connected && status?.smartphoneConnected !== false)
        ) {
          setQrImage(null);
          setQrExpired(false);
        }
      } catch (_) {
        // A próxima consulta tenta novamente enquanto o QR Code estiver aberto.
      } finally {
        polling = false;
      }
    }, ZAPI_STATUS_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [qrImage, restauranteId]);

  useEffect(() => {
    if (!qrImage) return undefined;
    const timeoutId = window.setTimeout(() => {
      setQrImage(null);
      setQrExpired(true);
    }, QR_CODE_EXPIRATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [qrImage]);

  const isBlocked = !bypassProvisioningGate && !loadingRestaurant && !hasInstanceToken;
  const isZapiUnavailable = !bypassProvisioningGate
    && hasInstanceToken
    && hasZapiCheck
    && isAtendabotWhatsAppUnavailable(zapiStatus);
  const isZapiOffline =
    !bypassProvisioningGate
    && !isZapiUnavailable
    && hasInstanceToken
    && hasZapiCheck
    && zapiStatus
    && (zapiStatus.connected === false || zapiStatus.smartphoneConnected === false);
  const showOverlay = !bypassProvisioningGate
    && (loadingRestaurant || isBlocked || isZapiUnavailable || isZapiOffline);

  const loadWhatsappQrCode = async () => {
    if (loadingQr) return;
    setLoadingQr(true);
    setQrExpired(false);
    try {
      const qr = await fetchZapiQrCode(restauranteId || undefined);
      setQrImage(qr?.image || null);
      if (!qr?.image) toast.error('Não foi possível obter a imagem do QR Code.');
    } catch (error: any) {
      setQrImage(null);
      if (isAtendabotWhatsAppUnavailableError(error)) {
        const payload = {
          ...(zapiStatus || {}),
          configured: true,
          connected: false,
          smartphoneConnected: false,
          instance_unavailable: true,
          error: 'instance_unavailable',
          checkedAt: Date.now(),
        };
        setZapiStatus(payload);
        setHasZapiCheck(true);
        if (restauranteId) {
          sessionStorage.setItem(`atendabot-zapi-status-${restauranteId}`, JSON.stringify(payload));
        }
        toast.error(WHATSAPP_INSTANCE_UNAVAILABLE_MESSAGE);
      } else {
        toast.error('Não foi possível gerar o QR Code agora. Tente novamente em instantes.');
      }
    } finally {
      setLoadingQr(false);
    }
  };

  return {
    zapiStatus,
    checkingZapiStatus,
    hasZapiCheck,
    qrImage,
    qrExpired,
    loadingQr,
    isBlocked,
    isZapiUnavailable,
    isZapiOffline,
    showOverlay,
    loadWhatsappQrCode,
  };
}
